/**
 * BlueKeys logo system — "Feels Like Home" (Direction G).
 * The mark is the b Coin: the wordmark's own lowercase "b" set on a deep-navy
 * circle — quiet, residential, legible from a favicon to a door plaque. The
 * wordmark is one confident lowercase word, a single colour, tightly tracked.
 *  - <KeyMark />            the b Coin (navy circle, white b) — light surfaces
 *  - <KeyMarkLight />       inverted Coin (white circle, navy b) — dark surfaces
 *  - <LogoLockup />         Coin + "bluekeys" wordmark (light surfaces)
 *  - <LogoLockupLight />    Coin + "bluekeys" wordmark (dark surfaces)
 * (Export names + props are unchanged so every consumer keeps working.)
 */

const NAVY = "#0e1e4d";

interface MarkProps {
  size?: number;
  /** kept for API compatibility; the Coin needs no gradient id */
  uid?: string;
  light?: boolean;
}

/** The b Coin — a lowercase "b" (the wordmark's own letter, Space Grotesk 700)
 *  centred on a solid circle. Navy circle + white b on light grounds; inverted
 *  (white circle + navy b) on dark. `size` is the circle diameter in px. */
function Coin({ size = 32, light = false }: MarkProps) {
  const bg = light ? "#ffffff" : NAVY;
  const fg = light ? NAVY : "#ffffff";
  return (
    <span
      role="img"
      aria-label="BlueKeys"
      style={{
        width: size,
        height: size,
        borderRadius: "50%",
        background: bg,
        color: fg,
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        fontFamily: "var(--font-display)",
        fontWeight: 700,
        fontSize: Math.round(size * 0.56),
        letterSpacing: "-0.04em",
        lineHeight: 1,
        flexShrink: 0,
      }}
    >
      b
    </span>
  );
}

export function KeyMark({ size = 32 }: MarkProps) {
  return <Coin size={size} />;
}

export function KeyMarkLight({ size = 32 }: MarkProps) {
  return <Coin size={size} light />;
}

interface LockupProps {
  /** wordmark font-size in px */
  size?: number;
}

/** Mark + wordmark, for light surfaces (white nav etc.). The small
 *  "Egypt" pill sitting next to the wordmark grounds the brand to the
 *  market we actually operate in — visitors no longer have to scroll
 *  to find out we're an Egypt-only platform. */
/** One confident lowercase word. Renders Latin "bluekeys" on LTR and Arabic
 *  "بلوكيز" on RTL — toggled purely by CSS (.bk-latin / .bk-ar in globals.css
 *  keyed off html[dir]) so no locale prop threads through every consumer, and
 *  SSR matches because the layout sets dir server-side. Single colour, always;
 *  never mixed-case, never a trailing mark. */
function Wordmark({ size, color }: { size: number; color: string }) {
  const base = {
    fontWeight: 700,
    fontSize: size,
    color,
    lineHeight: 1,
  } as const;
  return (
    <>
      <span className="bk-latin" style={{ ...base, fontFamily: "var(--font-display)", letterSpacing: "-0.04em" }}>
        bluekeys
      </span>
      <span className="bk-ar" style={{ ...base, fontFamily: "var(--font-arabic)", letterSpacing: 0 }}>
        بلوكيز
      </span>
    </>
  );
}

function EgyptPill({ light = false }: { light?: boolean }) {
  const cls = light
    ? "inline-flex items-center gap-1 text-[10px] sm:text-[10.5px] font-bold uppercase tracking-[0.08em] text-white/90 bg-white/15 px-1.5 py-0.5 rounded-full border border-white/25 leading-none"
    : "inline-flex items-center gap-1 text-[10px] sm:text-[10.5px] font-bold uppercase tracking-[0.08em] text-ss-blue bg-ss-blue-50 px-1.5 py-0.5 rounded-full border border-ss-blue/15 leading-none";
  return (
    <span className={cls} aria-label="Operating in Egypt">
      <span aria-hidden>🇪🇬</span>
      <span className="bk-latin">Egypt</span>
      <span className="bk-ar">مصر</span>
    </span>
  );
}

export function LogoLockup({ size = 22 }: LockupProps) {
  return (
    <span className="inline-flex items-center gap-2 sm:gap-2.5">
      <Wordmark size={size} color={NAVY} />
      <EgyptPill />
    </span>
  );
}

/** Wordmark, for dark surfaces (footer, hero overlay etc.) */
export function LogoLockupLight({ size = 22 }: LockupProps) {
  return (
    <span className="inline-flex items-center gap-2.5">
      <Wordmark size={size} color="#ffffff" />
      <EgyptPill light />
    </span>
  );
}

/**
 * Boutique Stays wordmark — text-only for now (no icon mark).
 * Uses Inter (--font-sans) at semibold weight.
 * "Boutique" in navy, "Stays" in gold.
 * For light surfaces (white nav, white backgrounds).
 */
export function BoutiqueStaysLockup({ size = 22 }: LockupProps) {
  return (
    <span className="inline-flex items-center gap-[0.25em]">
      <span
        style={{
          fontFamily: "var(--font-sans)",
          fontWeight: 700,
          fontSize: size,
          letterSpacing: "-0.02em",
          lineHeight: 1,
          color: "#1a2744",
        }}
      >
        Boutique
      </span>
      <span
        style={{
          fontFamily: "var(--font-sans)",
          fontWeight: 700,
          fontSize: size,
          letterSpacing: "-0.02em",
          lineHeight: 1,
          color: "#c9a84c",
        }}
      >
        Stays
      </span>
    </span>
  );
}

/**
 * Boutique Stays wordmark for dark surfaces (footer, hero overlay).
 * "Boutique" in white, "Stays" in gold.
 */
export function BoutiqueStaysLockupLight({ size = 22 }: LockupProps) {
  return (
    <span className="inline-flex items-center gap-[0.25em]">
      <span
        style={{
          fontFamily: "var(--font-sans)",
          fontWeight: 700,
          fontSize: size,
          letterSpacing: "-0.02em",
          lineHeight: 1,
          color: "#ffffff",
        }}
      >
        Boutique
      </span>
      <span
        style={{
          fontFamily: "var(--font-sans)",
          fontWeight: 700,
          fontSize: size,
          letterSpacing: "-0.02em",
          lineHeight: 1,
          color: "#c9a84c",
        }}
      >
        Stays
      </span>
    </span>
  );
}
